// Banque de questions v3 — Schéma Jolie Fille (Collection Radiant, 7 références).
//
// Flux complet :
//   Q1  — Type de maillot (1pcs / 2pcs / les deux) [MODE HOME UNIQUEMENT]
//   Q2  — Taille en cm (140-200) [cohérence silhouette, pas utilisé pour le sizing]
//   Q3a — Bande SG (65/70/.../110)
//   Q3b — Bonnet SG (A / B / C / D / DD/E / F / G+)
//   Q4  — Taille haut habituelle (XS / S / M / L / XL)
//   Q5  — Taille bas habituelle (XS / S / M / L / XL)
//   Q6  — Épaules vs hanches → V+40 / X+15+H+15+8+10 / A+40
//   Q7  — Taille vs hanches → X+30+8+15 / X+10+H+5 / H+30
//   Q8  — Épaules vs taille → 8+25+V+10 / X+10+8+5 / H+15
//   Q9  — Ventre → O+50+X-20+H-15 / O+15 / neutre
//   Q10 — Couvrance bas (minimale / cheeky / classique / couvrant)
//   Q11 — Question adaptative (5 cas selon profil)

import type { Question, ConditionContext } from './types';

// Helper : option neutre (pas de morpho impact)
function neutral() {
  return { A: 1, V: 1, H: 1, X: 1, '8': 1, O: 1 };
}

/**
 * En mode PRODUCT, on ne demande QUE les questions nécessaires au sizing
 * du produit courant (cf PPT Schema_Recommandation_JolieFille).
 *
 * Mapping productId → liste des questions à poser :
 *  - direct_top       (Adelina top)    → Q4 seulement
 *  - direct_bot       (tous les bas)   → Q5 seulement
 *  - palmyre_band     (Palmyre top)    → Q3a + Q3b + Q4 (+ Q11_diff si écart)
 *  - marianne_band_cup (Marianne top)  → Q3a + Q3b
 *  - nicole_band_bot  (Nicole 1pcs)    → Q3a + Q5
 */
const PRODUCT_QUESTIONS: Record<string, string[]> = {
  'JF-TOP-ADELINA':  ['q4_top'],
  'JF-BOT-ADELINA':  ['q5_bot'],
  'JF-TOP-PALMYRE':  ['q3_band', 'q3_cup', 'q4_top', 'q11_diff_size'],
  'JF-BOT-PALMYRE':  ['q5_bot'],
  'JF-TOP-MARIANNE': ['q3_band', 'q3_cup'],
  'JF-BOT-MARIANNE': ['q5_bot'],
  'JF-1P-NICOLE':    ['q3_band', 'q5_bot'],
};

/** True si la question doit être posée pour le produit courant en mode product. */
function neededForProduct(qid: string, ctx: ConditionContext): boolean {
  if (ctx.mode !== 'product') return true; // home → toujours
  const pid = ctx.currentProductId;
  if (!pid) return true; // safety : pas d'ID → on pose tout
  const list = PRODUCT_QUESTIONS[pid];
  if (!list) return true; // safety : produit inconnu → on pose tout
  return list.includes(qid);
}

export const QUESTIONS: Question[] = [
  // ============================================================
  // Q1 — Type de maillot (HOME UNIQUEMENT)
  // ============================================================
  {
    id: 'q1_type',
    kind: 'preference',
    mandatory: true,
    title: 'Type de maillot',
    prompt: 'Quel type de maillot de bain recherches-tu ?',
    options: [
      { id: 'two_pieces', label: 'Deux pièces', buttonLabel: 'Deux pièces', likelihood: neutral() },
      { id: 'one_piece', label: 'Une pièce', buttonLabel: 'Une pièce', likelihood: neutral() },
      { id: 'both', label: 'Les deux m\'intéressent', buttonLabel: 'Les deux', likelihood: neutral() },
    ],
    showIf: (ctx) => ctx.mode === 'home',
  },

  // ============================================================
  // Q2 — Taille en cm (home uniquement — pas utilisé pour le sizing produit)
  // ============================================================
  {
    id: 'q2_height',
    kind: 'measurement',
    mandatory: true,
    title: 'Taille',
    prompt: 'Quelle est ta taille en centimètres ?',
    options: [],
    measurement: { field: 'height', unit: 'cm', min: 140, max: 200, placeholder: 'ex : 168' },
    showIf: (ctx) => neededForProduct('q2_height', ctx),
  },

  // ============================================================
  // Q3a — Tour de bande SG
  // ============================================================
  {
    id: 'q3_band',
    kind: 'sizing',
    mandatory: true,
    title: 'Tour de bande (SG)',
    prompt: 'Quel est ton tour de bande de soutien-gorge ?',
    options: [
      { id: '65', label: '65', buttonLabel: '65', likelihood: neutral() },
      { id: '70', label: '70', buttonLabel: '70', likelihood: neutral() },
      { id: '75', label: '75', buttonLabel: '75', likelihood: neutral() },
      { id: '80', label: '80', buttonLabel: '80', likelihood: neutral() },
      { id: '85', label: '85', buttonLabel: '85', likelihood: neutral() },
      { id: '90', label: '90', buttonLabel: '90', likelihood: neutral() },
      { id: '95', label: '95', buttonLabel: '95', likelihood: neutral() },
      { id: '100', label: '100', buttonLabel: '100', likelihood: neutral() },
      { id: '105', label: '105', buttonLabel: '105', likelihood: neutral() },
      { id: '110', label: '110', buttonLabel: '110', likelihood: neutral() },
    ],
    showIf: (ctx) => neededForProduct('q3_band', ctx),
  },

  // ============================================================
  // Q3b — Bonnet (UNIQUE, pas de range)
  // ============================================================
  {
    id: 'q3_cup',
    kind: 'sizing',
    mandatory: true,
    title: 'Bonnet (SG)',
    prompt: 'Et quel est ton bonnet ?',
    options: [
      { id: 'A', label: 'A', buttonLabel: 'A', likelihood: neutral() },
      { id: 'B', label: 'B', buttonLabel: 'B', likelihood: neutral() },
      { id: 'C', label: 'C', buttonLabel: 'C', likelihood: neutral() },
      { id: 'D', label: 'D', buttonLabel: 'D', likelihood: neutral() },
      { id: 'DD_E', label: 'DD / E', buttonLabel: 'DD/E', likelihood: neutral() },
      { id: 'F', label: 'F', buttonLabel: 'F', likelihood: neutral() },
      { id: 'G_plus', label: 'G et plus', buttonLabel: 'G+', likelihood: neutral() },
    ],
    showIf: (ctx) => neededForProduct('q3_cup', ctx),
  },

  // ============================================================
  // Q4 — Taille haut habituelle
  // ============================================================
  {
    id: 'q4_top',
    kind: 'sizing',
    mandatory: true,
    title: 'Taille haut',
    prompt: 'Quelle taille portes-tu habituellement en haut ?',
    options: [
      { id: 'XS', label: 'XS', buttonLabel: 'XS', likelihood: neutral() },
      { id: 'S', label: 'S', buttonLabel: 'S', likelihood: neutral() },
      { id: 'M', label: 'M', buttonLabel: 'M', likelihood: neutral() },
      { id: 'L', label: 'L', buttonLabel: 'L', likelihood: neutral() },
      { id: 'XL', label: 'XL', buttonLabel: 'XL', likelihood: neutral() },
    ],
    showIf: (ctx) => neededForProduct('q4_top', ctx),
  },

  // ============================================================
  // Q5 — Taille bas habituelle
  // ============================================================
  {
    id: 'q5_bot',
    kind: 'sizing',
    mandatory: true,
    title: 'Taille bas',
    prompt: 'Et en bas ?',
    options: [
      { id: 'XS', label: 'XS', buttonLabel: 'XS', likelihood: neutral() },
      { id: 'S', label: 'S', buttonLabel: 'S', likelihood: neutral() },
      { id: 'M', label: 'M', buttonLabel: 'M', likelihood: neutral() },
      { id: 'L', label: 'L', buttonLabel: 'L', likelihood: neutral() },
      { id: 'XL', label: 'XL', buttonLabel: 'XL', likelihood: neutral() },
    ],
    showIf: (ctx) => neededForProduct('q5_bot', ctx),
  },

  // ============================================================
  // Q6 — Épaules vs hanches (POINTS SYSTEM)
  // ============================================================
  {
    id: 'q6_shoulders_hips',
    kind: 'morphology',
    mandatory: true,
    title: 'Épaules vs hanches',
    prompt: 'En te regardant de face, qu\'observes-tu ?',
    options: [
      {
        id: 'shoulders_wider',
        label: 'Mes épaules sont nettement plus larges que mes hanches',
        buttonLabel: 'Épaules plus larges',
        likelihood: neutral(),
        points: { V: 40 },
      },
      {
        id: 'balanced',
        label: 'Mes épaules et mes hanches sont à peu près pareilles',
        buttonLabel: 'À peu près pareilles',
        likelihood: neutral(),
        points: { X: 15, H: 15, '8': 10 },
      },
      {
        id: 'hips_wider',
        label: 'Mes hanches sont nettement plus larges que mes épaules',
        buttonLabel: 'Hanches plus larges',
        likelihood: neutral(),
        points: { A: 40 },
      },
    ],
    showIf: (ctx) => ctx.mode !== 'product',
  },

  // ============================================================
  // Q7 — Taille vs hanches
  // ============================================================
  {
    id: 'q7_waist_hips',
    kind: 'morphology',
    mandatory: true,
    title: 'Taille marquée ?',
    prompt: 'Ta taille par rapport à tes hanches ?',
    options: [
      {
        id: 'very_marked',
        label: 'Très fine, nettement marquée',
        buttonLabel: 'Très marquée',
        likelihood: neutral(),
        points: { X: 30, '8': 15 },
      },
      {
        id: 'slightly_marked',
        label: 'Légèrement marquée',
        buttonLabel: 'Légèrement',
        likelihood: neutral(),
        points: { X: 10, H: 5 },
      },
      {
        id: 'not_marked',
        label: 'Peu ou pas marquée',
        buttonLabel: 'Peu marquée',
        likelihood: neutral(),
        points: { H: 30 },
      },
    ],
    showIf: (ctx) => ctx.mode !== 'product',
  },

  // ============================================================
  // Q8 — Épaules vs taille
  // ============================================================
  {
    id: 'q8_shoulders_waist',
    kind: 'morphology',
    mandatory: true,
    title: 'Contraste épaules / taille',
    prompt: 'Tes épaules par rapport à ta taille ?',
    options: [
      {
        id: 'strong_contrast',
        label: 'Fort contraste — épaules nettement > taille',
        buttonLabel: 'Fort contraste',
        likelihood: neutral(),
        points: { '8': 25, V: 10 },
      },
      {
        id: 'moderate',
        label: 'Rapport modéré',
        buttonLabel: 'Modéré',
        likelihood: neutral(),
        points: { X: 10, '8': 5 },
      },
      {
        id: 'close',
        label: 'Épaules et taille assez proches',
        buttonLabel: 'Proches',
        likelihood: neutral(),
        points: { H: 15 },
      },
    ],
    showIf: (ctx) => ctx.mode !== 'product',
  },

  // ============================================================
  // Q9 — Ventre
  // ============================================================
  {
    id: 'q9_belly',
    kind: 'morphology',
    mandatory: true,
    title: 'Volume du ventre',
    prompt: 'Quand tu te regardes de profil, ton ventre est plutôt :',
    options: [
      {
        id: 'main_zone',
        label: 'Marqué — c\'est ma zone la plus visible',
        buttonLabel: 'Marqué',
        likelihood: neutral(),
        points: { O: 50, X: -20, H: -15 },
      },
      {
        id: 'slight',
        label: 'Léger — un peu rebondi mais pas dominant',
        buttonLabel: 'Léger',
        likelihood: neutral(),
        points: { O: 15 },
      },
      {
        id: 'no',
        label: 'Plat ou peu marqué — d\'autres zones se voient plus',
        buttonLabel: 'Plat',
        likelihood: neutral(),
        points: {},
      },
    ],
    showIf: (ctx) => ctx.mode !== 'product',
  },

  // ============================================================
  // Q10 — Couvrance bas
  // ============================================================
  {
    id: 'q10_coverage',
    kind: 'preference',
    mandatory: true,
    title: 'Couvrance bas',
    prompt: 'Quel niveau de couvrance préfères-tu pour le bas ?',
    options: [
      { id: 'minimal',   label: 'Très réduite — type ficelle / string',          buttonLabel: 'Très réduite',  likelihood: neutral() },
      { id: 'cheeky',    label: 'Légèrement couvrant — laisse voir les fesses',  buttonLabel: 'Léger',         likelihood: neutral() },
      { id: 'classique', label: 'Couverture moyenne — culotte standard',         buttonLabel: 'Moyenne',       likelihood: neutral() },
      { id: 'couvrant',  label: 'Bien couvrant — fesses entièrement couvertes',  buttonLabel: 'Couvrant',      likelihood: neutral() },
    ],
    showIf: (ctx) => ctx.mode !== 'product',
  },

  // ============================================================
  // Q11 — Question adaptative (5 cas)
  // Cas 1 : Bonnet DD/E+ → Maintien / Mise en valeur / Équilibre
  // Cas 2 : Bonnet A/B → Volume / Liberté / Équilibre
  // Cas 3 : Taille haut ≠ bas → Ajusté / Aise / Dépend
  // Cas 4 : Défaut → Léger / Modéré / Fort
  // Cas 5 : Morpho H ou V → Mise en valeur / Confort / Équilibre
  // ============================================================
  {
    id: 'q11_big_cup',
    kind: 'preference',
    mandatory: true,
    title: 'Priorité haut (bonnet généreux)',
    prompt: 'Pour le haut, qu\'est-ce qui compte le plus pour toi ?',
    options: [
      { id: 'maintien', label: 'Le maintien avant tout', buttonLabel: 'Maintien', likelihood: neutral() },
      { id: 'valeur', label: 'La mise en valeur de mon décolleté', buttonLabel: 'Mise en valeur', likelihood: neutral() },
      { id: 'equilibre', label: 'Un équilibre des deux', buttonLabel: 'Équilibre', likelihood: neutral() },
    ],
    showIf: (ctx) =>
      ctx.mode !== 'product' && (
        ctx.answers['q3_cup'] === 'DD_E' ||
        ctx.answers['q3_cup'] === 'F' ||
        ctx.answers['q3_cup'] === 'G_plus'
      ),
  },
  {
    id: 'q11_small_cup',
    kind: 'preference',
    mandatory: true,
    title: 'Priorité haut (petit bonnet)',
    prompt: 'Pour le haut, qu\'est-ce que tu cherches ?',
    options: [
      { id: 'volume', label: 'Créer du volume / de la mise en valeur', buttonLabel: 'Volume', likelihood: neutral() },
      { id: 'liberte', label: 'Liberté de mouvement avant tout', buttonLabel: 'Liberté', likelihood: neutral() },
      { id: 'equilibre', label: 'Un équilibre des deux', buttonLabel: 'Équilibre', likelihood: neutral() },
    ],
    showIf: (ctx) =>
      ctx.mode !== 'product' && (
        ctx.answers['q3_cup'] === 'A' || ctx.answers['q3_cup'] === 'B'
      ),
  },
  {
    id: 'q11_diff_size',
    kind: 'preference',
    mandatory: true,
    title: 'Tailles différentes',
    prompt: 'Tu n\'as pas la même taille en haut et en bas — quand tu es entre deux tailles, tu préfères :',
    options: [
      { id: 'tight', label: 'Plus petit (ajusté)', buttonLabel: 'Ajusté', likelihood: neutral() },
      { id: 'loose', label: 'Plus grand (à l\'aise)', buttonLabel: 'À l\'aise', likelihood: neutral() },
      { id: 'depends', label: 'Ça dépend du modèle', buttonLabel: 'Dépend', likelihood: neutral() },
    ],
    showIf: (ctx) => {
      const top = ctx.answers['q4_top'];
      const bot = ctx.answers['q5_bot'];
      const cup = ctx.answers['q3_cup'];
      if (ctx.mode === 'product') {
        // Mode product : Palmyre top uniquement, si bande EU et Q4 risquent de différer.
        // On la pose toujours par sécurité dès qu'on a les 2 infos (band + Q4).
        return ctx.currentProductId === 'JF-TOP-PALMYRE'
          && Boolean(ctx.answers['q3_band'] && top);
      }
      // Mode home : règle d'origine (bonnet C/D + tailles différentes)
      const middleCup = cup === 'C' || cup === 'D';
      return Boolean(middleCup && top && bot && top !== bot);
    },
  },
  {
    id: 'q11_hv_morpho',
    kind: 'preference',
    mandatory: true,
    title: 'Style maillot',
    prompt: 'Tu cherches plutôt à :',
    options: [
      { id: 'highlight', label: 'Mettre en valeur ma silhouette', buttonLabel: 'Mise en valeur', likelihood: neutral() },
      { id: 'comfort', label: 'Privilégier le confort', buttonLabel: 'Confort', likelihood: neutral() },
      { id: 'equilibre', label: 'Un équilibre des deux', buttonLabel: 'Équilibre', likelihood: neutral() },
    ],
    showIf: (ctx) => {
      if (ctx.mode === 'product') return false;
      const cup = ctx.answers['q3_cup'];
      const top = ctx.answers['q4_top'];
      const bot = ctx.answers['q5_bot'];
      const cupOk = cup === 'C' || cup === 'D';
      const sameSize = top && bot && top === bot;
      const isHV = ctx.topMorphology === 'H' || ctx.topMorphology === 'V';
      return Boolean(cupOk && sameSize && isHV);
    },
  },
  {
    id: 'q11_default',
    kind: 'preference',
    mandatory: true,
    title: 'Niveau de maintien',
    prompt: 'Quel niveau de maintien recherches-tu ?',
    options: [
      { id: 'leger', label: 'Léger — liberté de mouvement', buttonLabel: 'Léger', likelihood: neutral() },
      { id: 'modere', label: 'Modéré — bon compromis', buttonLabel: 'Modéré', likelihood: neutral() },
      { id: 'fort', label: 'Fort — maintien prioritaire', buttonLabel: 'Fort', likelihood: neutral() },
    ],
    showIf: (ctx) => {
      if (ctx.mode === 'product') return false;
      const cup = ctx.answers['q3_cup'];
      const top = ctx.answers['q4_top'];
      const bot = ctx.answers['q5_bot'];
      const cupOk = cup === 'C' || cup === 'D';
      const sameSize = top && bot && top === bot;
      const isHV = ctx.topMorphology === 'H' || ctx.topMorphology === 'V';
      // Cas par défaut : aucune des autres conditions Q11 ne s'applique
      return Boolean(cupOk && sameSize && !isHV);
    },
  },
];
